import { useState } from 'react'
import type {
  EditableThemeTokenName,
  EditableThemeTokensByVariant,
  ThemeVariant,
} from '@/shared/theme/theme-config'
import {
  currentEditableThemeTokens,
  generateRandomThemeTokens,
} from '@/shared/theme/theme-config'

function cloneEditableTokens(
  tokens: EditableThemeTokensByVariant,
): EditableThemeTokensByVariant {
  return {
    light: { ...tokens.light },
    dark: { ...tokens.dark },
  }
}

function hasEmptyTokenValue(draft: EditableThemeTokensByVariant) {
  return Object.values(draft).some((variantTokens) =>
    Object.values(variantTokens).some((value) => !value.trim()),
  )
}

export function useCustomThemeDraft({
  editableTokens,
}: {
  editableTokens: EditableThemeTokensByVariant
}) {
  const [draft, setDraft] = useState<EditableThemeTokensByVariant>(
    cloneEditableTokens(editableTokens),
  )
  const [error, setError] = useState<string | null>(null)

  const updateDraftToken = (
    variant: ThemeVariant,
    token: EditableThemeTokenName,
    value: string,
  ) => {
    setDraft((current) => ({
      light: {
        ...current.light,
        ...(variant === 'light' ? { [token]: value } : {}),
      },
      dark: {
        ...current.dark,
        ...(variant === 'dark' ? { [token]: value } : {}),
      },
    }))
    setError(null)
  }

  const resetDraft = () => {
    setDraft(cloneEditableTokens(currentEditableThemeTokens))
    setError(null)
  }

  const randomizeDraft = () => {
    setDraft(generateRandomThemeTokens())
    setError(null)
  }

  const validateDraft = () => {
    if (!hasEmptyTokenValue(draft)) {
      return true
    }

    setError('All editable theme tokens need a value before saving.')

    return false
  }

  return {
    draft,
    error,
    updateDraftToken,
    resetDraft,
    randomizeDraft,
    validateDraft,
  }
}
